import Decimal from 'decimal.js';
import type { NegotiationDataItemProps } from './NegociacaoFornecedor.types';

export enum CostNegotiationMode {
  NET_COST = 'NET_COST',
  ENTRY_COST = 'ENTRY_COST',
}

export const DEFAULT_COST_NEGOTIATION_MODE = CostNegotiationMode.NET_COST;

export type CostNegotiationModeValue = `${CostNegotiationMode}`;

// Mensagem fixa exibida quando o dado de origem congelado no fechamento nao esta disponivel. Literal
// unico para os dois motivos possiveis (negociacao fechada antes do congelamento existir, ou item cujo
// custo estava vazio na origem) -- os dois tem que ser indistinguiveis na tela, por decisao do dono do
// produto, entao nenhuma superficie pode acrescentar qualificador a este texto.
export const ENTRY_COST_UNAVAILABLE_MESSAGE = 'Dado de origem indisponível';

export const normalizeCostNegotiationMode = (value?: string | null): CostNegotiationMode => {
  if (value === CostNegotiationMode.ENTRY_COST) return CostNegotiationMode.ENTRY_COST;
  return DEFAULT_COST_NEGOTIATION_MODE;
};

// Resolve o modo de custo efetivo de uma linha da tela de fechada: o modo congelado na propria linha
// tem prioridade sobre o valor de tabela inteira vindo da consulta viva de configuracao do cliente.
// Uma negociacao fechada tem que ser exibida segundo a configuracao vigente no dia em que foi fechada,
// e o valor de tabela inteira e a configuracao de hoje -- por isso a decisao passa a ser por linha.
// Linha sem o campo (negociacao em andamento, ou fechada anterior a esta mudanca) cai no valor de
// tabela inteira, sem nenhuma diferenca em relacao ao comportamento de hoje.
export const resolveCostNegotiationModeForRow = (
  rowData: Pick<NegotiationDataItemProps, 'negotiationCostType'>,
  tableWideMode: CostNegotiationMode,
): CostNegotiationMode => {
  switch (rowData.negotiationCostType) {
    case 'ENTRY_COST':
      return CostNegotiationMode.ENTRY_COST;
    case 'NET_COST':
      return CostNegotiationMode.NET_COST;
    default:
      return tableWideMode;
  }
};

export const calculateLiquidCostFromEntryCost = (entryCost: number, purchaseTaxPercentage?: number | null): number => {
  // Custo de entrada ausente não é custo zero: é linha sem o insumo. Sem esta guarda,
  // `new Decimal(null)` lança durante o render e derruba a árvore inteira do React, deixando a tela
  // branca em vez de uma célula sem valor.
  if (entryCost === null || entryCost === undefined) return 0;

  const taxPercentage = new Decimal(purchaseTaxPercentage ?? 0);

  return new Decimal(entryCost).plus(new Decimal(entryCost).times(taxPercentage).dividedBy(100)).toDecimalPlaces(2).toNumber();
};

// Resolve o custo de entrada para exibicao. Quando o sinal de indisponibilidade da linha e verdadeiro,
// devolve ausencia (null) em vez de percorrer a cadeia de alternativas que troca o custo de entrada
// ausente pelo custo liquido -- essa cadeia existe de proposito para a negociacao em andamento (onde
// ausencia vinda de juncao viva legitimamente quer dizer "mostre o liquido"), mas reaproveitada sem
// mudanca no caminho fechado seria a degradacao silenciosa que esta fase proibe. Quem chama trata a
// ausencia exibindo a mensagem fixa, nunca formatando o valor nulo.
// Só os três campos de custo, para que a linha da resposta do servidor sirva tanto quanto a linha
// já enriquecida da grade: a célula do preço ideal de compra recebe a primeira.
export type CostFieldsForNegotiation = Pick<NegotiationDataItemProps, 'cost' | 'entryCost' | 'entryCostUnavailable'>;

export const getCurrentInputCostForNegotiation = (rowData: CostFieldsForNegotiation, mode: CostNegotiationMode): number | null => {
  if (mode === CostNegotiationMode.ENTRY_COST) {
    if (rowData.entryCostUnavailable) return null;
    return rowData.entryCost ?? rowData.cost ?? 0;
  }

  // Ausência sai como ausência, e não como zero: zero é um número, e o comprador negocia contra ele.
  // Quem precisa de número para calcular aplica o próprio recuo -- é o caso do custo líquido abaixo.
  return rowData.cost ?? null;
};

// A verba ACORDADA abate o custo, na mesma base do modo: em custo de entrada, a verba convertida ao
// fornecedor; em custo liquido, a do liquido. Sem acordo o abatimento e zero -- sellinAllowanceToSupplier
// recua para a sugestao quando ninguem acordou nada, e sugestao nao abate custo nenhum.
export const getAgreedAllowanceInCostBase = (
  rowData: Pick<NegotiationDataItemProps, 'sellinAllowance' | 'sellinAllowanceToSupplier'>,
  mode: CostNegotiationMode,
): number => {
  if (rowData.sellinAllowance === null || rowData.sellinAllowance === undefined) return 0;
  if (mode === CostNegotiationMode.ENTRY_COST) return rowData.sellinAllowanceToSupplier ?? 0;
  return rowData.sellinAllowance;
};

export const resolveLiquidCostForNegotiation =(rowData: NegotiationDataItemProps, costValue: number, mode: CostNegotiationMode): number => {
  if (mode === CostNegotiationMode.ENTRY_COST) {
    return calculateLiquidCostFromEntryCost(costValue, rowData.purchaseTaxPercentage);
  }

  return costValue;
};

export const getCurrentLiquidCostForNegotiation = (rowData: NegotiationDataItemProps, mode: CostNegotiationMode): number => {
  if (mode === CostNegotiationMode.ENTRY_COST) {
    if (rowData.entryCost === null || rowData.entryCost === undefined) return rowData.cost ?? 0;
    // rowData.entryCost ja foi confirmado presente acima; pelo contrato do servidor isso implica
    // entryCostUnavailable falso, entao getCurrentInputCostForNegotiation nunca devolve null aqui --
    // o fallback abaixo so existe para satisfazer o tipo, nunca e alcancado na pratica.
    const currentCost = getCurrentInputCostForNegotiation(rowData, mode) ?? rowData.cost ?? 0;
    return resolveLiquidCostForNegotiation(rowData, currentCost, mode);
  }

  return rowData.cost ?? 0;
};

export const getCurrentInputCostForVariation = (rowData: NegotiationDataItemProps, mode: CostNegotiationMode): number | null => {
  return getCurrentInputCostForNegotiation(rowData, mode);
};

export const resolveWorkingLiquidCostForNegotiation = (rowData: NegotiationDataItemProps, mode: CostNegotiationMode): number => {
  if (rowData.newCost === null || rowData.newCost === undefined) {
    return getCurrentLiquidCostForNegotiation(rowData, mode);
  }

  return resolveLiquidCostForNegotiation(rowData, rowData.newCost, mode);
};
